<template>
  <section v-if="canManage" class="company-logo-settings" aria-label="Логотип компании">
    <div class="logo-controls">
      <h2>Логотип компании</h2>
      <template v-if="canManage">
        <div class="file-selection">
          <input id="company-logo-file" ref="fileInput" class="file-input" type="file"
            accept=".png,.jpg,.jpeg,image/png,image/jpeg" :disabled="busy"
            :aria-invalid="!!error && !selectedFile"
            :aria-describedby="error ? 'company-logo-help company-logo-filename company-logo-error' : 'company-logo-help company-logo-filename'"
            @change="selectFile" />
          <label for="company-logo-file" class="file-select-control">Выберите файл</label>
          <span id="company-logo-filename" class="file-name" role="status" :title="selectedFile?.name || undefined">
            {{ selectedFile?.name || 'Файл не выбран' }}
          </span>
        </div>
        <p id="company-logo-help" class="helper">PNG, JPG или JPEG, до 2 МБ</p>
        <div class="actions">
          <Button :label="auth.user?.hasCompanyLogo ? 'Заменить логотип' : 'Добавить логотип'"
            size="small" severity="secondary" outlined class="logo-action"
            :disabled="busy || !selectedFile" :loading="busy" @click="upload" />
          <Button v-if="auth.user?.hasCompanyLogo" label="Удалить" size="small" severity="danger"
            text class="logo-action" :disabled="busy" @click="remove" />
        </div>
      </template>
      <p v-if="logoError" class="error" role="alert">
        {{ logoError }}
        <Button label="Повторить" size="small" text class="logo-action" @click="reload" />
      </p>
      <p v-if="error" id="company-logo-error" class="error" role="alert">{{ error }}</p>
      <p v-if="notice" class="notice" role="status">{{ notice }}</p>
    </div>
    <figure class="logo-preview">
      <div class="logo-preview-frame" :aria-busy="logoLoading">
        <img v-if="previewUrl || logoUrl" :src="previewUrl || logoUrl"
          :alt="previewUrl ? 'Предпросмотр выбранного логотипа' : 'Сохранённый логотип компании'" />
        <p v-else-if="logoLoading" class="preview-placeholder" role="status">Загрузка логотипа...</p>
        <p v-else class="preview-placeholder">{{ logoError ? 'Предпросмотр недоступен' : 'Логотип не добавлен' }}</p>
      </div>
      <figcaption>
        {{ previewUrl ? 'Новый логотип — ещё не сохранён' : logoUrl ? 'Сохранённый логотип' : 'Предпросмотр логотипа' }}
      </figcaption>
    </figure>
  </section>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import Button from 'primevue/button'
import { useAuthStore } from '@/modules/auth/store/auth.store'
import { usePermissionsStore } from '@/modules/auth/store/permissions.store'
import { useAppToast } from '@/shared/lib/useAppToast'
import { useCompanyLogo } from '@/modules/auth/services/useCompanyLogo'
import { uploadCompanyLogo, deleteCompanyLogo, validateCompanyLogo } from '@/modules/auth/api/company-logo.service'

const auth = useAuthStore()
const permissions = usePermissionsStore()
const toast = useAppToast()
const canManage = computed(() => permissions.hasPermission('ManageCompanyLogo'))
const { logoUrl, logoLoading, logoError, reload } = useCompanyLogo(
  () => auth.user?.hasCompanyLogo ? `${auth.user.id}:${auth.companyLogoVersion}` : '')
const selectedFile = ref(null)
const fileInput = ref(null)
const previewUrl = ref('')
const busy = ref(false)
const error = ref('')
const notice = ref('')
function clearSelection() {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
  previewUrl.value = ''
  selectedFile.value = null
  if (fileInput.value) fileInput.value.value = ''
}
function selectFile(event) {
  if (!canManage.value) return
  const file = event.target.files?.[0]
  clearSelection()
  error.value = ''
  notice.value = ''
  if (!file) return
  error.value = validateCompanyLogo(file)
  if (error.value) return
  selectedFile.value = file
  previewUrl.value = URL.createObjectURL(file)
}
async function mutate(operation, hasLogo) {
  if (busy.value || !canManage.value) return
  busy.value = true
  error.value = ''
  notice.value = ''
  const userId = auth.user?.id
  try {
    await operation()
    if (auth.user?.id !== userId) return
    auth.companyLogoChanged(hasLogo)
    clearSelection()
    notice.value = hasLogo ? 'Логотип сохранён.' : 'Логотип удалён.'
  } catch (e) {
    if (auth.user?.id !== userId) return
    if (e.status === 403 || e.response?.status === 403) {
      error.value = 'У вас больше нет разрешения на управление логотипом компании.'
      toast.warn(error.value)
      clearSelection()
      await permissions.fetchPermissions(true)
    } else {
      error.value = e.message || 'Не удалось изменить логотип. Попробуйте ещё раз.'
    }
  } finally {
    busy.value = false
  }
}
function upload() {
  if (selectedFile.value) return mutate(() => uploadCompanyLogo(selectedFile.value), true)
}
function remove() { return mutate(deleteCompanyLogo, false) }
watch(canManage, allowed => { if (!allowed) clearSelection() })
onBeforeUnmount(clearSelection)
</script>

<style scoped>
.company-logo-settings {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 200px;
  gap: 24px;
  align-items: center;
  min-width: 0;
}
.logo-controls { min-width: 0; }
h2 { margin: 0 0 18px; font-size: 14px; font-weight: 600; line-height: 1.5; color: #111; }
.file-selection { display: flex; align-items: center; flex-wrap: wrap; gap: 10px 12px; min-width: 0; }
/* Keep the native input focusable; its focus ring is shown on the label. */
.file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
.file-select-control {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 8px 14px;
  border: 1px solid #d8d8d8;
  border-radius: 999px;
  background: #fff;
  color: #111;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.file-select-control:hover { background: #f7f7f7; border-color: #111; }
.file-input:focus-visible + .file-select-control,
:deep(.logo-action.p-button:focus-visible) { outline: 2px solid #555; outline-offset: 3px; }
.file-input:disabled + .file-select-control { opacity: 0.6; cursor: not-allowed; }
.file-name {
  flex: 1 1 120px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #666;
}
.helper { margin: 8px 0 0; font-size: 11px; line-height: 1.5; color: #6b6b6b; }
.error, .notice { margin: 12px 0 0; font-size: 13px; line-height: 1.5; overflow-wrap: anywhere; }
.error { color: #b42318; }
.notice { color: #276749; }
.actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 18px; }
:deep(.logo-action.p-button) {
  min-height: 44px;
  max-width: 100%;
  padding: 0.5rem 0.85rem !important;
  font-size: 13px;
  font-weight: 500 !important;
}
:deep(.logo-action .p-button-label) { white-space: normal; overflow-wrap: anywhere; }
.logo-preview { width: 100%; min-width: 0; margin: 0; }
.logo-preview-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 140px;
  padding: 16px;
  border: 1px solid #ececec;
  border-radius: 12px;
  background: #fafafa;
}
img { display: block; width: 100%; height: 100%; object-fit: contain; min-width: 0; }
.preview-placeholder { margin: 0; text-align: center; font-size: 12px; line-height: 1.5; color: #6b6b6b; }
figcaption { min-height: 36px; margin-top: 8px; text-align: center; font-size: 12px; line-height: 1.5; color: #6b6b6b; }
@media (max-width: 760px) {
  .company-logo-settings { grid-template-columns: minmax(0, 1fr); gap: 20px; }
  .logo-preview { max-width: 240px; }
}
</style>
